import { useState } from "react";
import { ArrowRight, BadgeCheck, Loader2, Plus, Sparkles } from "lucide-react";
import { createHandoff, addExam } from "@/lib/studentsApi";
import { WhatsAppShare } from "@/components/students/SubscribeSuccess";

const KEY = "ez_student";
export const rememberStudent = (r) => { try { localStorage.setItem(KEY, JSON.stringify({ student_id: r.student_id, return_token: r.return_token, name: r.name })); } catch { /* storage blocked */ } };
export const recallStudent = () => { try { return JSON.parse(localStorage.getItem(KEY) || "null"); } catch { return null; } };
export const forgetStudent = () => { try { localStorage.removeItem(KEY); } catch { /* ignore */ } };

/** Shown instead of the form when this browser already subscribed: greet by name, one-tap add this article's exam, continue to AI. */
export function ReturningCard({ me, token, source, sessionId, onReset, onShare, cta }) {
  const [exams, setExams] = useState(me.exams);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const hint = me.exam_hint;
  const hintMissing = !!hint && !exams.some((e) => e.toLowerCase() === hint.toLowerCase());
  const live = me.ai_handoff_available !== false;
  const aiHome = (me.ai_url || "https://ai.entrancezone.com").replace(/\/$/, "") + "/?utm_source=entrancezone.com&utm_medium=subscribe_widget";

  const add = async () => {
    setBusy("add"); setErr("");
    try { const r = await addExam({ student_id: me.student_id, return_token: token, exam: hint, source, session_id: sessionId }); setExams(r.exams); }
    catch { setErr("Could not add this exam — please try again."); } finally { setBusy(""); }
  };
  const go = async () => {
    setBusy("go"); setErr("");
    try { const { redirect_url } = await createHandoff(me.student_id, me.grant); window.open(redirect_url, "_top"); }
    catch (e) { setErr(e?.response?.data?.detail || "Could not open EntranceZone AI."); setBusy(""); }
  };

  return (
    <div className="space-y-4" data-testid="returning-card">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 rounded-full bg-emerald-100 p-1.5 text-emerald-700"><BadgeCheck className="h-5 w-5" /></div>
        <div>
          <h2 className="font-serif text-lg font-semibold text-slate-900 md:text-xl" data-testid="returning-greeting">Welcome back, {me.first_name}!</h2>
          <p className="mt-1 text-sm text-slate-600">You're already getting EntranceZone alerts as <span className="font-mono text-xs font-semibold text-slate-800" data-testid="returning-student-id">{me.student_id}</span>.</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-1.5" data-testid="returning-exams">
        {exams.map((e) => <span key={e} className="rounded-full border border-slate-900 bg-slate-900 px-3 py-1 text-xs font-medium text-white">{e}</span>)}
        {hintMissing && (
          <button onClick={add} disabled={!!busy} data-testid="returning-add-exam"
            className="inline-flex items-center gap-1 rounded-full border border-dashed border-indigo-500 px-3 py-1 text-xs font-medium text-indigo-700 transition-[background-color,transform] hover:bg-indigo-50 active:scale-95 disabled:opacity-60">
            {busy === "add" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} Also get {hint} alerts
          </button>
        )}
      </div>
      <div className="rounded-xl bg-slate-900 p-4 text-white">
        <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-amber-300"><Sparkles className="h-3.5 w-3.5" /> {me.ai_connected ? "Your AI account is connected" : "Next step · free"}</div>
        <p className="mt-1.5 text-sm text-slate-200">{me.ai_connected ? "Pick up your rank & college prediction where you left off." : "Get your rank & college prediction and a personalised study plan on EntranceZone AI."}</p>
        {live ? (
          <button onClick={go} disabled={!!busy} data-testid="returning-continue-ai"
            className="mt-3 inline-flex items-center gap-2 rounded-lg bg-amber-400 px-4 py-2.5 text-sm font-semibold text-slate-900 transition-[background-color,transform] hover:bg-amber-300 active:scale-[0.98] disabled:opacity-60">
            {busy === "go" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}{cta || "Continue to EntranceZone AI"}<ArrowRight className="h-4 w-4" />
          </button>
        ) : (
          <a href={aiHome} target="_top" rel="noopener" data-testid="returning-explore-ai" className="mt-3 inline-flex items-center gap-2 rounded-lg bg-amber-400 px-4 py-2.5 text-sm font-semibold text-slate-900 hover:bg-amber-300">Explore EntranceZone AI<ArrowRight className="h-4 w-4" /></a>
        )}
        {err && <p className="mt-2 text-xs text-red-300" data-testid="returning-error">{err}</p>}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
        <WhatsAppShare shareUrl={source?.page_url} exam={hint || exams[0]} onShare={onShare} />
        <button onClick={onReset} data-testid="returning-not-you" className="text-[11px] text-slate-400 underline-offset-2 hover:text-slate-600 hover:underline">Not {me.first_name}? Subscribe with different details</button>
      </div>
    </div>
  );
}
